import React, { useState } from 'react';

import classNames from 'classnames';
import { useMutation } from 'react-query';
import { UserType } from 'src/accounts/types';
import { Toast } from 'src/common/components';
import { getUserInitial } from 'src/common/utils';

import { updateUser as updateUserApi } from '../api';
import { Action } from '../constants';
import { useAccountProfileContext } from '../context';

import UsersItemEditing from './UsersItemEditing';

type UsersItemProps = {
  user: UserType & { available_accounts_for_switch?: Array<{ id: string; name: string }> };
};

const UsersItem: React.FC<UsersItemProps> = ({ user }: UsersItemProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const { dispatch } = useAccountProfileContext();
  const [updateUser] = useMutation(updateUserApi);

  const handleSetIsUserActive = async (): Promise<void> => {
    try {
      await updateUser({
        id: user.id,
        is_active: !user.is_active,
        available_accounts_for_switch:
          user.available_accounts_for_switch?.map((acc) => acc?.id) ?? [],
      });
      dispatch({
        type: Action.UPDATE_USER,
        userId: user.id,
        data: { is_active: !user.is_active },
      });
      Toast.success('The user new state was saved.');
    } catch (err) {
      Toast.error('There was an error and the new state of the user could not be saved.');
    }
  };

  const handleSetIsEditing = (): void => {
    setIsEditing(!isEditing);
  };

  if (isEditing) return <UsersItemEditing user={user} setIsEditing={setIsEditing} />;

  return (
    <div className="flex flex-grow">
      <span className="my-auto items-center justify-center w-10 h-10 bg-primary-1.0 rounded-full sm:flex hidden mr-4">
        <p className="font-medium text-white-0.0">{getUserInitial(user)}</p>
      </span>
      <div className="flex items-center flex-grow">
        <div className="flex flex-col">
          <p className={classNames('font-semibold', !user.is_active ? 'text-gray-1.0' : '')}>
            {user.first_name} {user.last_name}
          </p>
          <div className="sm:flex sm:space-x-2 sm:mt-0 items-center mt-2">
            {user.is_account_manager && <p className="flex text-gray-1.0">Admin | </p>}
            {user.email && <p className="sm:mt-0 flex mt-2 text-gray-1.0">{user.email} | </p>}
            <p className="sm:mt-0 flex mt-2 text-gray-1.0">{user.username}</p>
          </div>
        </div>
        <div className="flex flex-shrink-0 ml-auto">
          <button
            type="button"
            className="icon-btn"
            data-testid="edit-user-btn"
            onClick={handleSetIsEditing}
          >
            <i className="m-auto icon-pencil_icon text-2xl text-primary-1.0" />
          </button>
          <button
            type="button"
            className="icon-btn"
            data-testid="deactivate-user-btn"
            onClick={handleSetIsUserActive}
          >
            <i
              className={classNames(
                'm-auto text-2xl text-primary-1.0',
                user.is_active ? 'icon-disable_icon' : 'icon-activate_icon'
              )}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsersItem;
